/**
 * LanguageSelect — the portal's language switcher.
 *
 * Replaces nine copies of the same <select> and its change handler, which were
 * pasted into common-header, five login screens, guardian-dashboard,
 * certificate-validation-page and student-nav. Each copy had its own class name
 * and two had drifted on the option order.
 *
 * Persistence is not handled here: i18n/config.ts already writes the choice to
 * localStorage from its `languageChanged` listener.
 */

import { useTranslation } from 'react-i18next';
import { Select } from './form-field';
import styles from './language-select.module.css';

/**
 * The three shipped locales. Order is deliberate: en, pt, es.
 *
 * No flag emoji. Windows ships no glyph for regional-indicator pairs, so 🇺🇸
 * renders as the bare letters "US" — the header read "us English" on every
 * Windows machine. `short` is for the compact variant, which sits in a bar
 * where the full name would not fit anyway.
 */
const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'pt', label: 'Português', short: 'PT' },
  { code: 'es', label: 'Español', short: 'ES' },
] as const;

interface LanguageSelectProps {
  /** Compact width for headers and toolbars. */
  compact?: boolean;
  className?: string;
  'aria-label'?: string;
}

export function LanguageSelect({ compact = false, className, ...rest }: LanguageSelectProps) {
  const { i18n, t } = useTranslation();

  // i18n.language may carry a region ("pt-BR"), which would match no <option>
  // and leave the select blank. Compare on the base tag.
  const current = LANGUAGES.find((l) => i18n.language?.split('-')[0] === l.code)?.code ?? 'en';

  return (
    <Select
      value={current}
      onChange={(event) => void i18n.changeLanguage(event.target.value)}
      aria-label={rest['aria-label'] ?? t('common.language')}
      className={[compact ? styles.compact : null, className].filter(Boolean).join(' ') || undefined}
    >
      {LANGUAGES.map((language) => (
        <option key={language.code} value={language.code}>
          {compact ? language.short : language.label}
        </option>
      ))}
    </Select>
  );
}
